import React, { useState, useEffect, useCallback } from 'react';
import './Products.css';
import './AllSellStyles.css';
import './CommonStyles.css';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import { getServiceStaffReport, getServiceStaffFilterOptions } from '../../services/reportApi';
import { formatDateTime } from '../../utils/dateUtils';

const ServiceStaffReport = () => {
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [searchTerm, setSearchTerm] = useState('');
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [activeSubTab, setActiveSubTab] = useState('orders');
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [filterOptions, setFilterOptions] = useState({ locations: {}, waiters: [] });
    const [filters, setFilters] = useState({ start_date: '', end_date: '', location_id: '', service_staff_id: '' });
    const [showFilters, setShowFilters] = useState(false);

    const subTabs = [
        { id: 'orders', label: 'Orders' },
        { id: 'line-orders', label: 'Line Orders' },
    ];

    const fetchData = useCallback(async (pg = 1, perPage = entriesPerPage) => {
        setLoading(true); setError(null);
        try {
            const res = await getServiceStaffReport({ ...filters, page: pg, per_page: perPage, type: activeSubTab });
            if (res.success) {
                setData(res.data.data || []);
                setTotal(res.data.total || 0);
                setLastPage(res.data.last_page || 1);
                setPage(res.data.current_page || pg);
            }
        } catch (e) { setError(e.message); } finally { setLoading(false); }
    }, [filters, entriesPerPage, activeSubTab]);

    useEffect(() => {
        fetchData(1, entriesPerPage);
        getServiceStaffFilterOptions().then(r => { if (r.success) setFilterOptions(r.data); }).catch(() => {});
    }, []);
    useEffect(() => { fetchData(1, entriesPerPage); }, [entriesPerPage, activeSubTab]);

    const handleFilterApply = () => { fetchData(1, entriesPerPage); setShowFilters(false); };
    const handleFilterReset = () => setFilters({ start_date: '', end_date: '', location_id: '', service_staff_id: '' });

    const filteredData = data.filter(r =>
        (r.invoice_no || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.service_staff || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.business_location || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.product_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleExport = (format) => {
        const headers = ['Date', 'Invoice No.', 'Service Staff', 'Location', 'Product', 'Qty', 'Unit Price', 'Discount', 'Tax', 'Total'];
        const rows = filteredData.map(r => [
            r.transaction_date ? formatDateTime(r.transaction_date) : '',
            r.invoice_no || '', r.service_staff || '', r.business_location || '',
            r.product_name || '', r.quantity || '', r.unit_price_inc_tax_formatted || '',
            r.line_discount_amount_formatted || '', r.item_tax_formatted || '', r.total_formatted || ''
        ]);
        const title = 'Service Staff Report'; const filename = 'service_staff_report';
        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();
        setShowExportDropdown(false);
    };

    const totalQty = filteredData.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0);
    const totalDiscount = filteredData.reduce((s, r) => s + (parseFloat(r.line_discount_amount) || 0), 0);
    const totalTax = filteredData.reduce((s, r) => s + (parseFloat(r.item_tax) || 0), 0);
    const totalAmount = filteredData.reduce((s, r) => s + (parseFloat(r.total) || 0), 0);

    const from = total === 0 ? 0 : (page - 1) * entriesPerPage + 1;
    const to = Math.min(page * entriesPerPage, total);
    const inputStyle = { padding: '0.35rem 0.6rem', border: '1px solid #e2e8f0', borderRadius: '5px', fontSize: '0.8rem', outline: 'none', background: '#fff' };
    const selStyle = { ...inputStyle, cursor: 'pointer' };

    return (
        <div style={{ paddingTop: '0.5rem' }}>

            {/* ── Filters ── */}
            <div style={{ marginBottom: '0.75rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => setShowFilters(v => !v)} style={{ ...inputStyle, cursor: 'pointer', background: showFilters ? '#3b82f6' : '#fff', color: showFilters ? '#fff' : '#334155', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    <i className="fa fa-filter" /> Filters
                </button>
                {showFilters && (<>
                    <input type="date" value={filters.start_date} onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
                    <input type="date" value={filters.end_date} onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} style={inputStyle} />
                    <select value={filters.location_id} onChange={e => setFilters(f => ({ ...f, location_id: e.target.value }))} style={selStyle}>
                        <option value="">All Locations</option>
                        {Array.isArray(filterOptions.locations)
                            ? filterOptions.locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)
                            : Object.entries(filterOptions.locations || {}).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                    </select>
                    <select value={filters.service_staff_id} onChange={e => setFilters(f => ({ ...f, service_staff_id: e.target.value }))} style={selStyle}>
                        <option value="">All Service Staff</option>
                        {Array.isArray(filterOptions.waiters)
                            ? filterOptions.waiters.map(w => <option key={w.id} value={w.id}>{w.first_name} {w.last_name}</option>)
                            : Object.entries(filterOptions.waiters || {}).map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                    </select>
                    <button onClick={handleFilterApply} style={{ ...inputStyle, background: '#3b82f6', color: '#fff', cursor: 'pointer', border: 'none' }}>Apply</button>
                    <button onClick={handleFilterReset} style={{ ...inputStyle, cursor: 'pointer' }}>Reset</button>
                </>)}
            </div>

            {/* ── Table Container ── */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>

                {/* Sub-tabs */}
                <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#fff' }}>
                    {subTabs.map(tab => (
                        <button key={tab.id} onClick={() => setActiveSubTab(tab.id)} style={{ padding: '1rem 1.25rem', background: 'none', border: 'none', borderTop: activeSubTab === tab.id ? '2px solid #3b82f6' : '2px solid transparent', color: activeSubTab === tab.id ? '#1e293b' : '#64748b', fontWeight: activeSubTab === tab.id ? '700' : '600', fontSize: '0.85rem', cursor: 'pointer' }}>
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Table controls */}
                <div className="table-controls" style={{ borderBottom: '1px solid #e2e8f0', padding: '1rem 1.25rem' }}>
                    <div className="table-controls-left">
                        <div className="entries-select">
                            <span>Show</span>
                            <select value={entriesPerPage} onChange={e => setEntriesPerPage(Number(e.target.value))}>
                                <option value={25}>25</option><option value={50}>50</option><option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                    </div>
                    <div className="table-controls-right">
                        <div className="export-dropdown-container" style={{ position: 'relative' }}>
                            <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.85rem', color: '#334155' }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                Export ▾
                            </button>
                            {showExportDropdown && (
                                <div className="export-dropdown-menu" style={{ position: 'absolute', top: '100%', right: 0, marginTop: '0.25rem', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 10, minWidth: '160px' }}>
                                    {[{ icon: 'fa-file-excel-o', label: 'Export CSV', action: () => handleExport('CSV') }, { icon: 'fa-file-excel-o', label: 'Export Excel', action: () => handleExport('Excel') }, { icon: 'fa-print', label: 'Print', action: () => handleExport('Print') }, { icon: 'fa-file-pdf-o', label: 'Export PDF', action: () => handleExport('PDF') }].map((item, i) => (
                                        <button key={i} onClick={() => { if (item.action) item.action(); setShowExportDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%', padding: '0.5rem 1rem', border: 'none', background: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.8125rem', color: '#475569' }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                                            <i className={`fa ${item.icon}`} style={{ width: '14px', textAlign: 'center' }} />{item.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        <input type="text" placeholder="Search ..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} style={{ padding: '0.4rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.8125rem', outline: 'none', width: '180px' }} />
                    </div>
                </div>

                {error && <div style={{ padding: '1rem', color: '#ef4444', fontSize: '0.875rem' }}>Error: {error}</div>}

                {/* Table */}
                <div style={{ overflowX: 'auto' }}>
                    <table className="products-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                {['Date', 'Invoice No.', 'Service Staff', 'Location', 'Product', 'Qty', 'Unit Price', 'Discount', 'Tax', 'Total'].map(h => (
                                    <th key={h} style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', fontWeight: '700', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="10" style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading...</td></tr>
                            ) : filteredData.length === 0 ? (
                                <tr><td colSpan="10" style={{ padding: '1rem', textAlign: 'center', fontSize: '0.85rem', color: '#64748b' }}>No data available in table</td></tr>
                            ) : filteredData.map((row, idx) => (
                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569', whiteSpace: 'nowrap' }}>{row.transaction_date ? formatDateTime(row.transaction_date) : ''}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#3b82f6', cursor: 'pointer' }}>{row.invoice_no}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.service_staff}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.business_location}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.product_name}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.quantity} {row.unit}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.unit_price_inc_tax_formatted}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.line_discount_amount_formatted}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#475569' }}>{row.item_tax_formatted}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#1e293b', fontWeight: '500' }}>{row.total_formatted}</td>
                                </tr>
                            ))}
                            {filteredData.length > 0 && (
                                <tr style={{ background: '#cbd5e1', borderTop: '2px solid #e2e8f0', fontWeight: '700' }}>
                                    <td colSpan="5" style={{ padding: '0.75rem 1rem', textAlign: 'center', fontSize: '0.85rem', color: '#0f172a' }}>Total:</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#0f172a' }}>{totalQty.toFixed(2)}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#0f172a' }}></td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#0f172a' }}>${totalDiscount.toFixed(2)}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#0f172a' }}>${totalTax.toFixed(2)}</td>
                                    <td style={{ padding: '0.75rem 1rem', fontSize: '0.8125rem', color: '#0f172a' }}>${totalAmount.toFixed(2)}</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="pagination" style={{ padding: '0.85rem 1.25rem', borderTop: '1px solid #e2e8f0' }}>
                    <div className="pagination-info" style={{ fontSize: '0.8125rem' }}>
                        {total > 0 ? `Showing ${from} to ${to} of ${total} entries` : 'No entries'}
                    </div>
                    <div className="pagination-controls">
                        <button className={`page-btn${page <= 1 ? ' disabled' : ''}`} onClick={() => { if (page > 1) fetchData(page - 1); }} disabled={page <= 1}>Previous</button>
                        {Array.from({ length: Math.min(lastPage, 5) }, (_, i) => { const pg = page <= 3 ? i + 1 : page - 2 + i; if (pg < 1 || pg > lastPage) return null; return <button key={pg} className={`page-btn${pg === page ? ' active' : ''}`} onClick={() => fetchData(pg)}>{pg}</button>; })}
                        <button className={`page-btn${page >= lastPage ? ' disabled' : ''}`} onClick={() => { if (page < lastPage) fetchData(page + 1); }} disabled={page >= lastPage}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceStaffReport;
